import React, { useMemo } from 'react';
import { Tag, ControlLink, Breakpoint, Stack, useBreakpoint } from '@moda/om';
import { PageFilterFiltersFragment } from '../../generated/types/PageFilterFiltersFragment';
import { titleize } from '../../lib/titleize';
import { compact } from '../../lib/compact';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCountry } from '../CountrySelector';
import { AVAILABILITIES } from './PageFilterAvailability';
import './PageFilterClear.scss';

interface Props {
  attributes: PageFilterFiltersFragment['attributes'];
  designers: PageFilterFiltersFragment['designers'];
  sizes: PageFilterFiltersFragment['sizes'];
}

export const PageFilterClear: React.FC<Props> = ({ attributes, designers, sizes }) => {
  const { country } = useCountry();
  const { params, removeFilter, clearAllFilters } = usePLPParams();

  const attributeFilters = useMemo(
    () =>
      params.attributes.map(value => {
        const attribute = attributes?.find(attribute => attribute?.slug === value);
        return {
          key: 'attributes' as const,
          value,
          label: titleize(attribute?.name || value)
        };
      }),
    [attributes, params.attributes]
  );

  const availabilityFilters = useMemo(
    () =>
      params.availability.map(value => {
        const availability = AVAILABILITIES.find(availability => availability.value === value);
        return {
          key: 'availability' as const,
          value,
          label: titleize(availability?.label || value)
        };
      }),
    [params.availability]
  );

  const colorFilters = useMemo(
    () =>
      params.colors.map(value => {
        return {
          key: 'colors' as const,
          value,
          label: titleize(value)
        };
      }),
    [params.colors]
  );

  const designerFilters = useMemo(
    () =>
      params.designers.map(value => {
        const designer = designers?.find(designer => designer?.slug === value);
        return {
          key: 'designers' as const,
          value,
          label: titleize(designer?.name || value)
        };
      }),
    [params.designers, designers]
  );

  const sizeFilters = useMemo(
    () =>
      params.sizes.map(value => {
        const size = sizes?.find(size => size?.filterValue === value);

        return {
          key: 'sizes' as const,
          value,
          label:
            size?.size ||
            (value.includes(':') ? value.split(':')[1].toUpperCase() : titleize(value))
        };
      }),
    [params.sizes, sizes]
  );

  const priceFilters = useMemo(
    () =>
      compact([
        params.pricemin != null
          ? {
              key: 'pricemin' as const,
              value: `min: ${params.pricemin}`,
              label: `${country.currencyCode} min: ${params.pricemin}`
            }
          : undefined,
        params.pricemax != null
          ? {
              key: 'pricemax' as const,
              value: `max: ${params.pricemax}`,
              label: `${country.currencyCode} max: ${params.pricemax}`
            }
          : undefined
      ]),
    [country.currencyCode, params.pricemin, params.pricemax]
  );

  const filters = useMemo(
    () => [
      ...availabilityFilters,
      ...attributeFilters,
      ...designerFilters,
      ...colorFilters,
      ...sizeFilters,
      ...priceFilters
    ],
    [
      availabilityFilters,
      attributeFilters,
      designerFilters,
      colorFilters,
      sizeFilters,
      priceFilters
    ]
  );
  const isMobile = useBreakpoint({ lt: 'sm' }).matches;

  if (filters.length === 0) return null;

  const tags = filters.map(filter => (
    <Tag
      key={filter.value}
      className="PageFilterClear__tag"
      onClick={() => isMobile && removeFilter(filter.key, filter.value)}
      onRemove={() => removeFilter(filter.key, filter.value)}
    >
      {filter.label}
    </Tag>
  ));

  const clearAll = filters.length > 1 && (
    <ControlLink className="PageFilterClear__clear-all" onClick={clearAllFilters}>
      Clear All
    </ControlLink>
  );

  return (
    <div className="PageFilterClear">
      <Breakpoint lt="md">
        <Stack space={3} alignItems="flex-start">
          <Stack className="PageFilterClear__filters" direction="horizontal" space={3}>
            {tags}
          </Stack>
          {clearAll}
        </Stack>
      </Breakpoint>

      <Breakpoint gt="md">
        {tags}
        {clearAll}
      </Breakpoint>
    </div>
  );
};
